/**
 * rAF driver with a fixed-timestep accumulator. Wall time only ever decides
 * HOW MANY steps run this frame — sim state at step N is identical no matter
 * how frames landed. Stops stepping entirely while the tab is hidden.
 *
 * ignoreHidden (debug) mode drives frames with setTimeout instead of rAF —
 * rAF never fires in hidden tabs — and raises the catch-up cap so the
 * throttled ~1 Hz timer of a hidden tab still advances the sim in bursts.
 * Pacing differs; the fixed-dt state trajectory does not.
 */

export const STEP_MS = 1000 / 60;
export const MAX_STEPS_PER_FRAME = 2;
const MAX_STEPS_PER_TICK_HIDDEN = 90;

export class RafLoop {
  private raf = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private active = false;
  private wanted = false;
  private destroyed = false;
  private last = -1;
  private acc = 0;

  private readonly onVisibility = () => this.sync();

  constructor(
    /** Called every animation frame with the number of steps due. */
    private readonly onSteps: (steps: number) => void,
    /** Called every frame with the wall frame time (fps/auto-tune only). */
    private readonly onFrame: (frameMs: number) => void,
    /** Debug: keep stepping while the document is hidden. */
    private readonly ignoreHidden = false,
  ) {
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  setActive(on: boolean): void {
    this.wanted = on && !this.destroyed;
    this.sync();
  }

  private sync(): void {
    const should = this.wanted && (this.ignoreHidden || !document.hidden);
    if (should && !this.active) {
      this.active = true;
      this.last = -1;
      this.acc = 0;
      this.schedule();
    } else if (!should && this.active) {
      this.active = false;
      cancelAnimationFrame(this.raf);
      clearTimeout(this.timer);
    }
  }

  private schedule(): void {
    if (this.ignoreHidden) {
      this.timer = setTimeout(() => this.frame(performance.now()), STEP_MS);
    } else {
      this.raf = requestAnimationFrame(this.frame);
    }
  }

  private readonly frame = (now: number) => {
    if (!this.active) return;
    this.schedule();
    const maxSteps = this.ignoreHidden
      ? MAX_STEPS_PER_TICK_HIDDEN
      : MAX_STEPS_PER_FRAME;
    let steps = 0;
    if (this.last >= 0) {
      // fps accounting gets the WALL frame time (a 10 fps device must read
      // as 10 fps); only the step accumulator is catch-up-clamped.
      this.onFrame(now - this.last);
      this.acc += Math.min(now - this.last, maxSteps * STEP_MS);
      while (this.acc >= STEP_MS && steps < maxSteps) {
        this.acc -= STEP_MS;
        steps += 1;
      }
      // Drop backlog beyond one pending step — catch-up is capped, not owed.
      this.acc = Math.min(this.acc, STEP_MS);
    }
    this.last = now;
    this.onSteps(steps);
  };

  destroy(): void {
    // Latched: setActive(true) after destroy (e.g. from a late export
    // finally) must never revive the loop.
    this.destroyed = true;
    this.setActive(false);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }
}
